import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CategoryFilter = ({ selectedCategory, onCategoryChange, className = "" }) => {
  const categories = [
    {
      id: 'all',
      name: 'All Products',
      icon: 'Package',
      count: 67,
      color: 'text-text-primary',
      bgColor: 'bg-muted'
    },
    {
      id: 'ERCP',
      name: 'ERCP',
      icon: 'Eye',
      count: 6,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      description: 'ERCP procedures and accessories'
    },
    {
      id: 'Gastrostomy',
      name: 'Gastrostomy',
      icon: 'Heart',
      count: 2,
      color: 'text-success',
      bgColor: 'bg-success/10',
      description: 'PEG kits and replacement tubes'
    },
    {
      id: 'Hemostasis',
      name: 'Hemostasis',
      icon: 'Droplets',
      count: 3,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      description: 'Bleeding control devices'
    },
    {
      id: 'SEMS Stenting',
      name: 'SEMS Stenting',
      icon: 'Zap',
      count: 6,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      description: 'Self-expanding metallic stents'
    },
    {
      id: 'Accessories',
      name: 'Accessories',
      icon: 'Settings',
      count: 3,
      color: 'text-info',
      bgColor: 'bg-info/10',
      description: 'Endoscopic accessories'
    },
    {
      id: 'Polypectomy',
      name: 'Polypectomy',
      icon: 'Scissors',
      count: 2,
      color: 'text-purple',
      bgColor: 'bg-purple/10',
      description: 'Polyp removal devices'
    },
    {
      id: 'Rapid Urease Test',
      name: 'Rapid Urease Test',
      icon: 'TestTube',
      count: 2,
      color: 'text-orange',
      bgColor: 'bg-orange/10',
      description: 'H. pylori detection tests'
    },
    {
      id: 'Obesity Management',
      name: 'Obesity Management',
      icon: 'Activity',
      count: 1,
      color: 'text-pink',
      bgColor: 'bg-pink/10',
      description: 'Weight management solutions'
    },
    {
      id: 'Endo-Urology Access',
      name: 'Endo-Urology Access',
      icon: 'Zap',
      count: 3,
      color: 'text-blue',
      bgColor: 'bg-blue/10',
      description: 'Urological access devices'
    },
    {
      id: 'Endo-Urology Dilation',
      name: 'Endo-Urology Dilation',
      icon: 'ArrowUpDown',
      count: 12,
      color: 'text-green',
      bgColor: 'bg-green/10',
      description: 'Urological dilation instruments'
    },
    {
      id: 'Endo-Urology Drainage',
      name: 'Endo-Urology Drainage',
      icon: 'Droplets',
      count: 7,
      color: 'text-cyan',
      bgColor: 'bg-cyan/10',
      description: 'Drainage catheters and stents'
    },
    {
      id: 'Endo-Urology Stone Management',
      name: 'Endo-Urology Stone Management',
      icon: 'Circle',
      count: 10,
      color: 'text-indigo',
      bgColor: 'bg-indigo/10',
      description: 'Stone retrieval and management'
    },
    {
      id: 'Endo-Urology TURP',
      name: 'Endo-Urology TURP',
      icon: 'Scissors',
      count: 7,
      color: 'text-red',
      bgColor: 'bg-red/10',
      description: 'TURP instruments and accessories'
    },
    {
      id: 'Laparoscopy Access',
      name: 'Laparoscopy Access',
      icon: 'Eye',
      count: 1,
      color: 'text-yellow',
      bgColor: 'bg-yellow/10',
      description: 'Laparoscopic access devices'
    },
    {
      id: 'Laparoscopy Accessories',
      name: 'Laparoscopy Accessories',
      icon: 'Settings',
      count: 1,
      color: 'text-teal',
      bgColor: 'bg-teal/10',
      description: 'Laparoscopic accessories'
    },
    {
      id: 'Wound Drainage',
      name: 'Wound Drainage',
      icon: 'Heart',
      count: 1,
      color: 'text-rose',
      bgColor: 'bg-rose/10',
      description: 'Wound drainage systems'
    }
  ];

  return (
    <div className={`space-y-3 ${className}`}>
      <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wide mb-4">
        Product Categories
      </h3>
      <div className="space-y-2">
        {categories?.map((category) => (
          <motion.button
            key={category?.id}
            onClick={() => onCategoryChange(category?.id)}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
              selectedCategory === category?.id
                ? `${category?.bgColor} border-current ${category?.color} shadow-sm`
                : 'bg-card border-border hover:bg-muted hover:border-border'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedCategory === category?.id ? category?.bgColor : 'bg-muted'
              }`}>
                <Icon 
                  name={category?.icon} 
                  size={18} 
                  className={selectedCategory === category?.id ? category?.color : 'text-text-secondary'} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className={`font-medium text-sm ${
                    selectedCategory === category?.id ? category?.color : 'text-text-primary'
                  }`}>
                    {category?.name}
                  </h4>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    selectedCategory === category?.id 
                      ? `${category?.bgColor} ${category?.color}` 
                      : 'bg-muted text-text-secondary'
                  }`}>
                    {category?.count}
                  </span>
                </div>
                
                {category?.description && (
                  <p className={`text-xs mt-1 ${
                    selectedCategory === category?.id ? category?.color + '/80' : 'text-text-secondary'
                  }`}>
                    {category?.description}
                  </p>
                )}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
      {/* Featured Category Highlight */}
      <div className="mt-6 p-4 bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Star" size={16} className="text-primary" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-text-primary mb-1">
              Featured Category
            </h4>
            <p className="text-xs text-text-secondary mb-2">
              Explore our latest endoscopy solutions with advanced imaging technology.
            </p>
            <button
              onClick={() => onCategoryChange('endoscopy')}
              className="text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-200"
            >
              View Endoscopy Products →
            </button>
          </div>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h4 className="text-sm font-semibold text-text-primary mb-3">Quick Stats</h4>
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-text-secondary">Total Products:</span>
            <span className="font-medium text-text-primary">67</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-secondary">Categories:</span>
            <span className="font-medium text-success">18</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-secondary">GDPMD Certified:</span>
            <span className="font-medium text-primary">67</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-text-secondary">In Stock:</span>
            <span className="font-medium text-warning">67</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryFilter;